import { Crown, Headphones, LayoutGrid, Info } from "lucide-react";

/**
 * Persona View selector — pure presentation-layer switcher.
 *
 * Does NOT change any analysis, AI, RBAC, or backend logic. Only
 * controls which existing sections of the incident page are visible.
 *
 * Props:
 *  - value: "fullView" | "leadership" | "operations"
 *  - onChange: (next) => void
 */
const PERSONAS = [
  {
    key: "fullView",
    label: "Full View",
    icon: LayoutGrid,
    focus: "Default · complete CRE analysis exactly as-is.",
  },
  {
    key: "leadership",
    label: "Leadership",
    icon: Crown,
    focus:
      "Leadership focus: impact, cause, confidence, actions, and approval.",
  },
  {
    key: "operations",
    label: "Operations",
    icon: Headphones,
    focus:
      "Operations focus: triage, technical reasoning, runbook, actions, and audit.",
  },
];

export default function PersonaSwitcher({ value, onChange }) {
  const active = PERSONAS.find((p) => p.key === value) || PERSONAS[0];

  return (
    <div
      data-testid="persona-switcher"
      className="cre-surface rounded-sm p-4 md:p-5"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0">
          <div className="cre-overline text-slate-500">Persona View</div>
          <div className="mt-0.5 text-[12.5px] text-slate-600 leading-relaxed max-w-2xl">
            Full View shows the complete incident analysis. Persona views
            tailor the page without changing the underlying analysis.
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Persona view"
          className="inline-flex items-center gap-1 p-1 rounded-md bg-slate-100 border border-slate-200 shrink-0 self-start"
        >
          {PERSONAS.map((p) => {
            const Icon = p.icon;
            const selected = value === p.key;
            return (
              <button
                key={p.key}
                role="tab"
                aria-selected={selected}
                data-testid={`persona-tab-${p.key}`}
                onClick={() => onChange(p.key)}
                className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-sm text-[12px] font-semibold transition-colors ${
                  selected
                    ? "bg-white text-violet-700 shadow-sm border border-violet-200"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        data-testid="persona-focus-banner"
        className="mt-3 flex items-start gap-2 text-[12px] text-slate-500"
      >
        <Info className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" strokeWidth={2} />
        <span>{active.focus}</span>
        <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest text-violet-700 bg-violet-100 border border-violet-200">
          Presentation filter only
        </span>
      </div>
    </div>
  );
}
